<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields['title'] }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div>
          <label class="label-input">Section Title</label>
          <v-text-field v-model="fields.title" label="Title" />
        </div>
        <div class="mb-6">
          <div class="d-flex align-center gap-2 mb-4">
            <label for="imageShow" class="label-input mb-0 mr-2 cursor-pointer"> Image </label>
            <input type="checkbox" v-model="fields.image.show" id="imageShow" />
          </div>
          <div class="group-item">
            <v-select v-model="fields.image.position" :items="positionOptions" label="Position" />
            <v-row class="mt-2">
              <v-col cols="12" md="6">
                <v-text-field
                  type="number"
                  v-model.number="fields.image.widthDesktop"
                  label="Image Size (Desktop)"
                  min="1"
                  dense
                  hide-details
                  suffix="px"
                  :rules="[v => v > 0 || 'Image size must be greater than 0']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  type="number"
                  v-model.number="fields.image.widthMobile"
                  label="Image Size (Mobile)"
                  min="1"
                  dense
                  hide-details
                  suffix="px"
                  :rules="[v => v > 0 || 'Image size must be greater than 0']"
                />
              </v-col>
            </v-row>
            <div class="mb-4 mt-4">
              <UploadImage
                ref="safeCheckoutImageUpload"
                @callback="handleUploadSafeCheckoutImage"
                :image="{ imageSrc: fields.image.url }"
                :height="`150`"
                :typeUpload="'image'"
                @callbackDelete="handleRemoveSafeCheckoutImage"
              />
            </div>
          </div>
        </div>
        <div>
          <label class="label-input">Content</label>
          <ckeditor :editor="editor" v-model="fields['content']" :config="editorConfig" />
        </div>
        <div class="mt-6">
          <label class="label-input">Section Settings</label>
          <div class="group-item">
            <SectionPadding
              class="mb-6"
              :fields="fields.sectionPadding"
              :reset-padding="{ topDesktop: 4, bottomDesktop: 4, topMobile: 4, bottomMobile: 4 }"
            />
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Container Colors</div>
              <v-row>
                <v-col md="4" cols="12">
                  <ColorPicker
                    v-model="fields.colors.backgroundColor"
                    name="background-color"
                    label="Background Color"
                    default-color="#ffffff"
                  />
                </v-col>
                <v-col md="4" cols="12">
                  <ColorPicker
                    v-model="fields.colors.containerBackgroundColor"
                    name="container-background-color"
                    label="Container Background Color"
                    default-color="#ffffff"
                  />
                </v-col>
                <v-col md="4" cols="12">
                  <ColorPicker
                    v-model="fields.colors.textColor"
                    name="text-color"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
              <div class="mt-6">
                <div class="font-weight-medium mb-3">Container Layout</div>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-select v-model="fields.layout.alignItems" :items="alignItemsOptions" label="Align Items" />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      type="number"
                      v-model.number="fields.layout.gap"
                      label="Gap"
                      min="0"
                      suffix="px"
                      :rules="[v => v >= 0 || 'Gap must be greater than or equal 0']"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-checkbox v-model="fields.layout.mobileColumn" label="Column layout on mobile" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-checkbox v-model="fields.layout.borderEnabled" label="Show Border" />
                  </v-col>
                  <v-col cols="12" md="9">
                    <ColorPicker
                      v-model="fields.layout.borderColor"
                      name="container-border-color"
                      label="Border Color"
                      default-color="#e5e7eb"
                    />
                  </v-col>
                </v-row>
              </div>
              <div class="mt-6">
                <div class="font-weight-medium mb-6">Container Border Radius</div>
                <v-text-field
                  type="number"
                  v-model.number="fields.borderRadius"
                  label="Border Radius"
                  min="0"
                  dense
                  hide-details
                  suffix="px"
                  :rules="[v => v >= 0 || 'Border Radius must be greater than or equal 0']"
                />
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '@/views/website/products/components/description/common/html/update';
import ColorPicker from './common/color-picker.vue';
import SectionPadding from './common/section-padding.vue';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';

export default {
  name: 'safe-checkout-02-section-editor',
  components: {
    ColorPicker,
    SectionPadding,
    UploadImage,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      positionOptions: [
        {
          text: 'Left',
          value: 'left',
        },
        {
          text: 'Right',
          value: 'right',
        },
      ],
      alignItemsOptions: [
        {
          text: 'Center',
          value: 'center',
        },
        {
          text: 'Start',
          value: 'flex-start',
        },
        {
          text: 'End',
          value: 'flex-end',
        },
      ],
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: '',
        content: '',
        image: {
          url: '',
          position: 'left',
          show: true,
          widthDesktop: 160,
          widthMobile: 70,
        },
        layout: {
          alignItems: 'flex-start',
          borderEnabled: false,
          borderColor: '#e5e7eb',
          gap: 10,
          mobileColumn: false,
        },
        colors: {
          textColor: '#000000',
          backgroundColor: '#ffffff',
          containerBackgroundColor: '#F7EEE7',
        },
        sectionPadding: {
          topDesktop: 4,
          bottomDesktop: 4,
          topMobile: 4,
          bottomMobile: 4,
        },
        borderRadius: 4,
      },
    };
  },
  created() {
    const extract = this.sectionEditor.sectionExtract || {};
    this.fields = {
      ...this.fields,
      ...extract,
      image: {
        ...this.fields.image,
        ...(extract.image || {}),
      },
      layout: {
        ...this.fields.layout,
        ...(extract.layout || {}),
      },
      colors: {
        ...this.fields.colors,
        ...(extract.colors || {}),
      },
      sectionPadding: {
        ...this.fields.sectionPadding,
        ...(extract.sectionPadding || {}),
      },
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionGuaranteedSafeCheckout02(
        this.sectionEditor.section.html,
        this.fields,
      );
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields['title'],
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleUploadSafeCheckoutImage(obj) {
      if (obj && obj.imageSrc) {
        this.fields.image.url = obj.imageSrc;
      }
    },
    handleRemoveSafeCheckoutImage() {
      this.fields.image.url = '';
    },
  },
};
</script>

<style lang="scss">
.ck-editor__editable_inline {
  min-height: 150px;
}
.ck-editor__editable {
  min-height: 150px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
